import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY_VERSION = "v1";
const STORAGE_KEY_PREFIX = `claudia:prompt-history:${STORAGE_KEY_VERSION}`;
const PROMPT_HISTORY_CHANGE_EVENT = "df:claudia-prompt-history-change";

export const CLAUDIA_PROMPT_HISTORY_LIMIT = 50;

type PromptHistoryScope = {
  projectId: string;
  userId: string;
};

type PromptHistoryChangeDetail = {
  entries: string[];
  storageKey: string;
};

function normalizePrompt(value: string): string {
  return value.trim();
}

function sanitizePromptEntries(value: unknown): string[] {
  if (!Array.isArray(value)) return [];

  const deduped = new Set<string>();
  const normalizedEntries: string[] = [];

  for (const entry of value) {
    if (typeof entry !== "string") continue;
    const normalized = normalizePrompt(entry);
    if (!normalized || deduped.has(normalized)) continue;
    deduped.add(normalized);
    normalizedEntries.push(normalized);
    if (normalizedEntries.length >= CLAUDIA_PROMPT_HISTORY_LIMIT) {
      break;
    }
  }

  return normalizedEntries;
}

export function buildClaudiaPromptHistoryStorageKey(scope: PromptHistoryScope): string {
  return `${STORAGE_KEY_PREFIX}:${scope.userId}:${scope.projectId}`;
}

export function readClaudiaPromptHistory(scope: PromptHistoryScope): string[] {
  try {
    const raw = localStorage.getItem(buildClaudiaPromptHistoryStorageKey(scope));
    if (!raw) return [];
    return sanitizePromptEntries(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeClaudiaPromptHistory(scope: PromptHistoryScope, entries: string[]) {
  const normalizedEntries = sanitizePromptEntries(entries);
  const storageKey = buildClaudiaPromptHistoryStorageKey(scope);

  try {
    localStorage.setItem(storageKey, JSON.stringify(normalizedEntries));
  } catch {
    // localStorage unavailable
  }

  window.dispatchEvent(
    new CustomEvent<PromptHistoryChangeDetail>(PROMPT_HISTORY_CHANGE_EVENT, {
      detail: {
        storageKey,
        entries: normalizedEntries,
      },
    })
  );

  return normalizedEntries;
}

export function appendClaudiaPromptHistoryEntry(
  scope: PromptHistoryScope,
  prompt: string
): string[] {
  const normalizedPrompt = normalizePrompt(prompt);
  const existingEntries = readClaudiaPromptHistory(scope);
  if (!normalizedPrompt) return existingEntries;

  return writeClaudiaPromptHistory(scope, [
    normalizedPrompt,
    ...existingEntries.filter((entry) => entry !== normalizedPrompt),
  ]);
}

function subscribeClaudiaPromptHistory(
  scope: PromptHistoryScope,
  onChange: (entries: string[]) => void
) {
  const storageKey = buildClaudiaPromptHistoryStorageKey(scope);

  const handlePromptHistoryChange = (event: Event) => {
    const customEvent = event as CustomEvent<PromptHistoryChangeDetail>;
    if (customEvent.detail?.storageKey !== storageKey) return;
    onChange(customEvent.detail.entries);
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== storageKey) return;
    onChange(readClaudiaPromptHistory(scope));
  };

  window.addEventListener(PROMPT_HISTORY_CHANGE_EVENT, handlePromptHistoryChange as EventListener);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(
      PROMPT_HISTORY_CHANGE_EVENT,
      handlePromptHistoryChange as EventListener
    );
    window.removeEventListener("storage", handleStorage);
  };
}

export function useClaudiaPromptHistory(params: {
  projectId: string | null | undefined;
  userId: string | null | undefined;
}) {
  const scope = useMemo<PromptHistoryScope | null>(() => {
    if (!params.projectId || !params.userId) return null;
    return {
      projectId: params.projectId,
      userId: params.userId,
    };
  }, [params.projectId, params.userId]);
  const [entries, setEntries] = useState<string[]>([]);

  useEffect(() => {
    if (!scope) {
      setEntries([]);
      return;
    }

    setEntries(readClaudiaPromptHistory(scope));
    return subscribeClaudiaPromptHistory(scope, setEntries);
  }, [scope]);

  const recordPrompt = useCallback(
    (prompt: string) => {
      if (!scope) return [];
      const nextEntries = appendClaudiaPromptHistoryEntry(scope, prompt);
      setEntries(nextEntries);
      return nextEntries;
    },
    [scope]
  );

  return {
    entries,
    recordPrompt,
  };
}
